/**
 * AssistantPill — "Masz pytanie?" floating pill + mini-popover
 * Controlled, minimalist, contextual
 */
import { useState, useRef, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { MessageCircle, Send, X, Loader2, ArrowRight } from 'lucide-react';
import axios from 'axios';
import { API } from '@/utils/api';

export default function AssistantPill() {
  const [open, setOpen] = useState(false);
  const [question, setQuestion] = useState('');
  const [answer, setAnswer] = useState('');
  const [loading, setLoading] = useState(false);
  const [hint, setHint] = useState(null);
  const [hintCta, setHintCta] = useState(null);
  const [hintRoute, setHintRoute] = useState(null);
  const [dismissed, setDismissed] = useState(() => {
    // Check if user already dismissed a hint this session
    return !!sessionStorage.getItem('apisai_hint_dismissed');
  });
  const inputRef = useRef(null);
  const location = useLocation();
  const navigate = useNavigate();

  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };

  // Get page name from route
  const getPage = () => {
    const path = location.pathname;
    if (path.includes('/grupy')) return 'grupy';
    if (path.includes('/galeria')) return 'galeria';
    if (path.includes('/profil')) return 'profil';
    if (path.includes('/leady')) return 'leady';
    if (path.includes('/strona')) return 'strona';
    if (path.includes('/ustawienia')) return 'ustawienia';
    if (path.includes('/booster') || path.includes('/marketing')) return 'marketing';
    if (path.includes('/kalendarz') || path.includes('/auto-publikacja')) return 'publikacja';
    if (path.includes('/psy') || path.includes('/mioty') || path.includes('/reproduktory')) return 'zwierzeta';
    return 'dashboard';
  };

  const dismissHint = () => {
    setDismissed(true);
    sessionStorage.setItem('apisai_hint_dismissed', '1');
  };

  // Fetch ghost hint on page change — skip if AssistantPanel is on the page
  useEffect(() => {
    if (!token || dismissed) return;
    setAnswer('');
    // Don't show ghost hint when AssistantPanel is already visible (avoids duplication)
    const hasPanel = document.querySelector('[data-testid="assistant-panel"]');
    if (hasPanel) {
      setHint(null);
      return;
    }
    const page = getPage();
    axios.get(`${API}/assistant/context?page=${page}`, { headers })
      .then(res => {
        setHint(res.data.hint);
        setHintCta(res.data.cta);
        setHintRoute(res.data.route);
      })
      .catch(() => {});
  }, [location.pathname]); // eslint-disable-line

  useEffect(() => {
    if (open && inputRef.current) inputRef.current.focus();
  }, [open]);

  const handleAsk = async () => {
    if (!question.trim() || loading) return;
    setLoading(true);
    setAnswer('');
    try {
      const res = await axios.post(`${API}/assistant/ask`, {
        message: question,
        session_id: null,
      }, { headers });
      setAnswer(res.data.reply || res.data.answer || 'Nie mogę teraz odpowiedzieć.');
    } catch {
      setAnswer('Coś poszło nie tak. Spróbuj ponownie.');
    } finally {
      setLoading(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleAsk();
    }
  };

  if (!token) return null;

  return (
    <>
      {/* ═══ GHOST HINT — contextual inline tooltip (hidden on xl: when right panel is visible) ═══ */}
      <AnimatePresence>
        {hint && !dismissed && !open && (
          <motion.div
            initial={{ opacity: 0, y: -10, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -10, scale: 0.95 }}
            transition={{ delay: 1.5, duration: 0.3 }}
            className="fixed top-[72px] right-6 z-40 max-w-[320px] xl:hidden"
            data-testid="ghost-hint"
          >
            <div className="bg-white rounded-2xl border border-zinc-200 shadow-[0_8px_32px_rgba(0,0,0,0.12)] p-4">
              <div className="flex items-start gap-3">
                <div className="w-7 h-7 rounded-lg bg-[#F47A3B]/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <MessageCircle className="w-3.5 h-3.5 text-[#F47A3B]" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-[13px] text-zinc-900 leading-relaxed">{hint}</p>
                  {hintCta && hintRoute && (
                    <button
                      onClick={() => { dismissHint(); navigate(hintRoute); }}
                      className="mt-2 text-[12px] font-semibold text-[#F47A3B] hover:underline flex items-center gap-1"
                      data-testid="ghost-hint-cta"
                    >
                      {hintCta} <ArrowRight className="w-3 h-3" />
                    </button>
                  )}
                </div>
                <button
                  onClick={() => dismissHint()}
                  className="text-[#C4C9D1] hover:text-zinc-400 p-0.5 -mt-1 -mr-1"
                >
                  <X className="w-3.5 h-3.5" />
                </button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* ═══ FLOATING PILL ═══ */}
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: 20, scale: 0.9 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.9 }}
            className="fixed top-[72px] right-6 z-50 w-[340px]"
            data-testid="assistant-popover"
          >
            <div className="bg-white rounded-2xl border border-zinc-200 shadow-[0_12px_48px_rgba(0,0,0,0.15)] overflow-hidden">
              {/* Header */}
              <div className="px-4 py-3 bg-[#09090B] flex items-center justify-between">
                <span className="text-[13px] font-semibold text-white">Asystent ApisAI</span>
                <button onClick={() => { setOpen(false); setAnswer(''); setQuestion(''); }} className="text-white/50 hover:text-white">
                  <X className="w-4 h-4" />
                </button>
              </div>

              {/* Answer area */}
              {answer && (
                <div className="p-4 border-b border-[#F3F4F6] max-h-[200px] overflow-y-auto">
                  <p className="text-[13px] text-zinc-900 leading-relaxed whitespace-pre-line">{answer}</p>
                </div>
              )}

              {loading && (
                <div className="p-4 flex items-center gap-2 border-b border-[#F3F4F6]">
                  <Loader2 className="w-4 h-4 animate-spin text-[#F47A3B]" />
                  <span className="text-[12px] text-zinc-400">Myślę...</span>
                </div>
              )}

              {/* Input */}
              <div className="p-3 flex items-center gap-2">
                <input
                  ref={inputRef}
                  type="text"
                  value={question}
                  onChange={e => setQuestion(e.target.value)}
                  onKeyDown={handleKeyDown}
                  placeholder="Napisz pytanie..."
                  className="flex-1 text-[13px] px-3 py-2 rounded-lg border border-zinc-200 focus:border-[#F47A3B] focus:outline-none focus:ring-1 focus:ring-[#F47A3B]/20 bg-[#FAFBFC]"
                  data-testid="assistant-input"
                />
                <button
                  onClick={handleAsk}
                  disabled={!question.trim() || loading}
                  className="w-8 h-8 rounded-lg bg-[#F47A3B] hover:bg-[#E06A2E] text-white flex items-center justify-center disabled:opacity-40 transition-all"
                  data-testid="assistant-send"
                >
                  <Send className="w-3.5 h-3.5" />
                </button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* ═══ PILL BUTTON ═══ */}
      <motion.button
        onClick={() => { setOpen(!open); dismissHint(); }}
        className={`fixed top-[18px] right-[220px] z-50 flex items-center gap-2 px-3.5 py-2 rounded-full shadow-lg transition-all ${
          open
            ? 'bg-[#09090B] text-white shadow-[0_4px_20px_rgba(0,0,0,0.3)]'
            : 'bg-[#18181B] text-zinc-300 border border-[#27272A] shadow-[0_4px_20px_rgba(0,0,0,0.2)] hover:bg-[#27272A] hover:text-white'
        }`}
        whileHover={{ scale: 1.03 }}
        whileTap={{ scale: 0.97 }}
        data-testid="assistant-pill"
      >
        {open ? (
          <X className="w-4 h-4" />
        ) : (
          <>
            <MessageCircle className="w-4 h-4 text-[#F47A3B]" />
            <span className="text-[12px] font-semibold">Masz pytanie?</span>
          </>
        )}
      </motion.button>
    </>
  );
}
